import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { api } from '@/App';
import { usePhotoPreview } from '@/components/PhotoPreview';
import { StandingMovement } from '@/components/StandingMovement';

// Short code + color map for volleyball positions
const POSITION_META = {
  'Setter':          { short: 'S',   cls: 'bg-purple-100 text-purple-800' },
  'Outside Hitter':  { short: 'OH',  cls: 'bg-blue-100 text-blue-800' },
  'Opposite Hitter': { short: 'OP',  cls: 'bg-green-100 text-green-800' },
  'Middle Blocker':  { short: 'MB',  cls: 'bg-orange-100 text-orange-800' },
  'Libero':          { short: 'L',   cls: 'bg-red-100 text-red-800' },
};

const PositionBadge = ({ position, rank }) => {
  if (!position) return null;
  const meta = POSITION_META[position] || { short: position, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span
      title={`${rank} preference: ${position}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${meta.cls}`}
    >
      <span className="opacity-60">{rank}</span>
      <span>{meta.short}</span>
    </span>
  );
};

const Row = ({ label, value }) => (
  value ? (
    <div className="flex justify-between gap-3 py-1.5 border-b border-gray-100">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 font-medium text-right">{value}</span>
    </div>
  ) : null
);

export default function UserProfileModal({ userId, open, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const previewPhoto = usePhotoPreview();

  useEffect(() => {
    if (!open || !userId) return;
    let cancel = false;
    setLoading(true);
    setError(null);
    setProfile(null);
    api.get(`/users/${userId}/public-profile`)
      .then((res) => { if (!cancel) setProfile(res.data); })
      .catch((err) => {
        if (!cancel) setError(err?.response?.data?.detail || 'Failed to load user profile');
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [userId, open]);

  const locationParts = [profile?.current_city, profile?.current_country].filter(Boolean).join(', ');
  const currentClub = profile?.current_club || profile?.club_names;
  const hasPositions = profile?.position_1st || profile?.position_2nd || profile?.position_3rd;

  // Effective rating (admin override if set, else community) — shown consistently with game lists
  const effRating = profile
    ? (typeof profile.effective_rating === 'number'
        ? profile.effective_rating
        : (profile.rating > 0 ? profile.rating : (profile.community_rating_average || 0)))
    : 0;
  const effSource = profile
    ? (profile.rating_source || (profile.rating > 0 ? 'admin' : (profile.community_rating_average > 0 ? 'community' : 'none')))
    : 'none';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="user-profile-modal">
        <DialogHeader>
          <DialogTitle>Player Profile</DialogTitle>
          <DialogDescription>Basic information about this player.</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-gray-500">Loading...</div>
        )}
        {error && (
          <div className="py-6 text-center text-red-600" data-testid="user-profile-error">{error}</div>
        )}

        {profile && !loading && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {profile.display_photo ? (
                <img
                  src={profile.display_photo}
                  alt={profile.first_name}
                  loading="lazy"
                  onClick={() => previewPhoto(profile.profile_photo_url || profile.display_photo, `${profile.first_name || ''} ${profile.last_name || ''}`.trim())}
                  className="w-20 h-20 rounded-full object-cover border-2 border-orange-300 cursor-zoom-in"
                  data-testid="user-profile-photo"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-xl font-bold">
                  {profile.first_name?.[0]}{profile.last_name?.[0]}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-lg font-bold text-gray-900 truncate" data-testid="user-profile-name">
                  {profile.first_name} {profile.last_name}
                </div>
                {profile.preferred_name && (
                  <div className="text-sm text-gray-500 truncate">"{profile.preferred_name}"</div>
                )}
                <div className="flex gap-2 mt-1 flex-wrap">
                  {profile.is_admin && <Badge className="bg-orange-100 text-orange-700">Admin</Badge>}
                  {effRating > 0 && (
                    <Badge className="bg-purple-100 text-purple-700" data-testid="user-profile-rating">
                      ⭐ Rating: {Number(effRating).toFixed(2)}
                      {effSource === 'community' && profile.total_games_rated ? ` (${profile.total_games_rated})` : ''}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {hasPositions && (
              <div>
                <div className="text-sm text-gray-500 mb-1.5">Preferred Positions</div>
                <div className="flex flex-wrap gap-1.5">
                  <PositionBadge position={profile.position_1st} rank="1st" />
                  <PositionBadge position={profile.position_2nd} rank="2nd" />
                  <PositionBadge position={profile.position_3rd} rank="3rd" />
                </div>
              </div>
            )}

            <div className="border-t pt-3">
              <Row label="Experience" value={profile.volleyball_experience} />
              <Row
                label="Standing"
                value={profile.standing?.rank
                  ? <span className="inline-flex items-center gap-1.5">{`${profile.standing.rank_label} / ${profile.standing.total}`}<StandingMovement standing={profile.standing} /></span>
                  : 'Unrated'}
              />
              <Row
                label="Rating"
                value={
                  effRating > 0
                    ? (effSource === 'admin'
                        ? `${Number(effRating).toFixed(2)} / 10 (set by admin)`
                        : `${Number(effRating).toFixed(2)} / 10 (${profile.total_games_rated || 0} game${profile.total_games_rated === 1 ? '' : 's'} rated)`)
                    : 'Not yet rated'
                }
              />
              <Row label="Current Club" value={currentClub} />
              <Row label="Current Level" value={profile.current_level} />
              <Row label="Highest Level" value={profile.highest_level} />
              <Row label="Gender" value={profile.gender} />
              <Row label="From" value={profile.country_of_origin} />
              <Row label="Location" value={locationParts} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
